import {
  ABORTED_REASON_VALUES,
  COMPLETED_JOB_OUTCOME_VALUES,
} from '@/features/jobs/lib/job-outcome-options'
import type { JobSeverity, JobType } from '@/features/jobs/types/job'
import type { ReportCategoryChartType } from '@/features/reports/components/report-category-chart'
import { SLA_CATEGORY_VALUES } from '@/features/reports/lib/format-sla-category'
import { appendSortParams } from '@/lib/column-sort'
import {
  DEFAULT_JOBS_REPORT_SORT,
  parseJobsReportSort,
  type JobsReportSort,
} from '@/features/reports/lib/jobs-report-sort'
import {
  emptyAbortedJobsReportFilters,
  emptyCompletedJobsReportFilters,
  emptyServiceLevelAgreementReportFilters,
  type AbortedJobsReportFilters,
  type CompletedJobOutcome,
  type CompletedJobsReportFilters,
  type JobsReportBaseFilters,
  type ServiceLevelAgreementReportFilters,
  type SlaCategory,
} from '@/features/reports/types/jobs-report'

const JOB_TYPES: readonly JobType[] = ['new_installation', 'remediation']
const JOB_SEVERITIES: readonly JobSeverity[] = ['expedited', 'urgent', 'bau']
const CHART_TYPES: readonly ReportCategoryChartType[] = ['pie', 'bar']

export type AbortedJobsReportSearchState = {
  filters: AbortedJobsReportFilters
  page: number
  chartType: ReportCategoryChartType
  sort: JobsReportSort
}

export type CompletedJobsReportSearchState = {
  filters: CompletedJobsReportFilters
  page: number
  chartType: ReportCategoryChartType
  sort: JobsReportSort
}

export type ServiceLevelAgreementReportSearchState = {
  filters: ServiceLevelAgreementReportFilters
  page: number
  chartType: ReportCategoryChartType
  sort: JobsReportSort
}

const parseOptionalEnum = <T extends string>(
  value: string | null,
  allowed: readonly T[]
): T | undefined => {
  if (!value) {
    return undefined
  }

  return (allowed as readonly string[]).includes(value) ? (value as T) : undefined
}

const parseOptionalPositiveInt = (value: string | null) => {
  if (!value) {
    return undefined
  }

  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return undefined
  }

  return parsed
}

const parseJobsReportBaseFilters = (
  searchParams: URLSearchParams
): JobsReportBaseFilters => ({
  date_raised: searchParams.get('date_raised') ?? '',
  job_type: parseOptionalEnum(searchParams.get('job_type'), JOB_TYPES),
  location: searchParams.get('location') ?? '',
  raised_by_user_id: parseOptionalPositiveInt(
    searchParams.get('raised_by_user_id')
  ),
  assigned_partner_id: parseOptionalPositiveInt(
    searchParams.get('assigned_partner_id')
  ),
  completed_at_from: searchParams.get('completed_at_from') ?? '',
  completed_at_to: searchParams.get('completed_at_to') ?? '',
  completed_by_user_id: parseOptionalPositiveInt(
    searchParams.get('completed_by_user_id')
  ),
})

const parseChartType = (searchParams: URLSearchParams): ReportCategoryChartType =>
  parseOptionalEnum(searchParams.get('chart'), CHART_TYPES) ?? 'pie'

const parsePage = (searchParams: URLSearchParams) =>
  parseOptionalPositiveInt(searchParams.get('page')) ?? 1

const appendBaseParams = (
  params: URLSearchParams,
  filters: JobsReportBaseFilters
) => {
  const dateRaised = filters.date_raised.trim()
  const location = filters.location.trim()
  const completedAtFrom = filters.completed_at_from.trim()
  const completedAtTo = filters.completed_at_to.trim()

  if (dateRaised) {
    params.set('date_raised', dateRaised)
  }

  if (location) {
    params.set('location', location)
  }

  if (filters.job_type) {
    params.set('job_type', filters.job_type)
  }

  if (filters.raised_by_user_id) {
    params.set('raised_by_user_id', String(filters.raised_by_user_id))
  }

  if (filters.assigned_partner_id) {
    params.set('assigned_partner_id', String(filters.assigned_partner_id))
  }

  if (completedAtFrom) {
    params.set('completed_at_from', completedAtFrom)
  }

  if (completedAtTo) {
    params.set('completed_at_to', completedAtTo)
  }

  if (filters.completed_by_user_id) {
    params.set('completed_by_user_id', String(filters.completed_by_user_id))
  }
}

const appendPagingChartAndSort = (
  params: URLSearchParams,
  page: number,
  chartType: ReportCategoryChartType,
  sort: JobsReportSort
) => {
  if (chartType !== 'pie') {
    params.set('chart', chartType)
  }

  if (page > 1) {
    params.set('page', String(page))
  }

  appendSortParams(params, sort, DEFAULT_JOBS_REPORT_SORT)
}

export const parseAbortedJobsReportSearchParams = (
  searchParams: URLSearchParams
): AbortedJobsReportSearchState => ({
  page: parsePage(searchParams),
  chartType: parseChartType(searchParams),
  sort: parseJobsReportSort(
    searchParams.get('sort'),
    searchParams.get('direction')
  ),
  filters: {
    ...parseJobsReportBaseFilters(searchParams),
    aborted_reason: parseOptionalEnum(
      searchParams.get('aborted_reason'),
      ABORTED_REASON_VALUES
    ),
  },
})

export const buildAbortedJobsReportSearchParams = (
  filters: AbortedJobsReportFilters,
  page = 1,
  chartType: ReportCategoryChartType = 'pie',
  sort: JobsReportSort = DEFAULT_JOBS_REPORT_SORT
) => {
  const params = new URLSearchParams()
  appendBaseParams(params, filters)

  if (filters.aborted_reason) {
    params.set('aborted_reason', filters.aborted_reason)
  }

  appendPagingChartAndSort(params, page, chartType, sort)
  return params
}

export const defaultAbortedJobsReportSearchParams = () =>
  buildAbortedJobsReportSearchParams(emptyAbortedJobsReportFilters(), 1)

const withSearch = (path: string, params: URLSearchParams) => {
  const search = params.toString()
  return search ? `${path}?${search}` : path
}

export const abortedJobsByReasonPath = (
  filters: AbortedJobsReportFilters,
  page = 1,
  chartType: ReportCategoryChartType = 'pie',
  sort: JobsReportSort = DEFAULT_JOBS_REPORT_SORT
) =>
  withSearch(
    '/reports/aborted-jobs',
    buildAbortedJobsReportSearchParams(filters, page, chartType, sort)
  )

export const abortedJobsByPartnerPath = (
  filters: AbortedJobsReportFilters,
  page = 1,
  chartType: ReportCategoryChartType = 'pie',
  sort: JobsReportSort = DEFAULT_JOBS_REPORT_SORT
) =>
  withSearch(
    '/reports/aborted-jobs-by-partner',
    buildAbortedJobsReportSearchParams(filters, page, chartType, sort)
  )

export const parseCompletedJobsReportSearchParams = (
  searchParams: URLSearchParams
): CompletedJobsReportSearchState => ({
  page: parsePage(searchParams),
  chartType: parseChartType(searchParams),
  sort: parseJobsReportSort(
    searchParams.get('sort'),
    searchParams.get('direction')
  ),
  filters: {
    ...parseJobsReportBaseFilters(searchParams),
    outcome: parseOptionalEnum(
      searchParams.get('outcome'),
      COMPLETED_JOB_OUTCOME_VALUES
    ) as CompletedJobOutcome | undefined,
  },
})

export const buildCompletedJobsReportSearchParams = (
  filters: CompletedJobsReportFilters,
  page = 1,
  chartType: ReportCategoryChartType = 'pie',
  sort: JobsReportSort = DEFAULT_JOBS_REPORT_SORT
) => {
  const params = new URLSearchParams()
  appendBaseParams(params, filters)

  if (filters.outcome) {
    params.set('outcome', filters.outcome)
  }

  appendPagingChartAndSort(params, page, chartType, sort)
  return params
}

export const defaultCompletedJobsReportSearchParams = () =>
  buildCompletedJobsReportSearchParams(emptyCompletedJobsReportFilters(), 1)

export const completedJobsByOutcomePath = (
  filters: CompletedJobsReportFilters,
  page = 1,
  chartType: ReportCategoryChartType = 'pie',
  sort: JobsReportSort = DEFAULT_JOBS_REPORT_SORT
) =>
  withSearch(
    '/reports/completed-jobs',
    buildCompletedJobsReportSearchParams(filters, page, chartType, sort)
  )

export const completedJobsByPartnerPath = (
  filters: CompletedJobsReportFilters,
  page = 1,
  chartType: ReportCategoryChartType = 'pie',
  sort: JobsReportSort = DEFAULT_JOBS_REPORT_SORT
) =>
  withSearch(
    '/reports/completed-jobs-by-partner',
    buildCompletedJobsReportSearchParams(filters, page, chartType, sort)
  )

export const parseServiceLevelAgreementReportSearchParams = (
  searchParams: URLSearchParams
): ServiceLevelAgreementReportSearchState => ({
  page: parsePage(searchParams),
  chartType: parseChartType(searchParams),
  sort: parseJobsReportSort(
    searchParams.get('sort'),
    searchParams.get('direction')
  ),
  filters: {
    ...parseJobsReportBaseFilters(searchParams),
    severity: parseOptionalEnum(searchParams.get('severity'), JOB_SEVERITIES),
    sla_category: parseOptionalEnum(
      searchParams.get('sla_category'),
      SLA_CATEGORY_VALUES
    ),
  },
})

export const buildServiceLevelAgreementReportSearchParams = (
  filters: ServiceLevelAgreementReportFilters,
  page = 1,
  chartType: ReportCategoryChartType = 'pie',
  sort: JobsReportSort = DEFAULT_JOBS_REPORT_SORT
) => {
  const params = new URLSearchParams()
  appendBaseParams(params, filters)

  if (filters.severity) {
    params.set('severity', filters.severity)
  }

  if (filters.sla_category) {
    params.set('sla_category', filters.sla_category)
  }

  appendPagingChartAndSort(params, page, chartType, sort)
  return params
}

export const defaultServiceLevelAgreementReportSearchParams = () =>
  buildServiceLevelAgreementReportSearchParams(
    emptyServiceLevelAgreementReportFilters(),
    1
  )

export const serviceLevelAgreementPath = (
  filters: ServiceLevelAgreementReportFilters,
  page = 1,
  chartType: ReportCategoryChartType = 'pie',
  sort: JobsReportSort = DEFAULT_JOBS_REPORT_SORT
) =>
  withSearch(
    '/reports/service-level-agreement',
    buildServiceLevelAgreementReportSearchParams(filters, page, chartType, sort)
  )

export const serviceLevelAgreementByPartnerPath = (
  filters: ServiceLevelAgreementReportFilters,
  page = 1,
  chartType: ReportCategoryChartType = 'pie',
  sort: JobsReportSort = DEFAULT_JOBS_REPORT_SORT
) =>
  withSearch(
    '/reports/service-level-agreement-by-partner',
    buildServiceLevelAgreementReportSearchParams(filters, page, chartType, sort)
  )

export const buildJobsReportQuery = (
  filters?: JobsReportBaseFilters & {
    aborted_reason?: string
    outcome?: string
    severity?: JobSeverity
    sla_category?: SlaCategory
  },
  page = 1,
  sort: JobsReportSort = DEFAULT_JOBS_REPORT_SORT
) => {
  const params = new URLSearchParams()

  if (filters) {
    appendBaseParams(params, filters)

    if (filters.aborted_reason) {
      params.set('aborted_reason', filters.aborted_reason)
    }

    if (filters.outcome) {
      params.set('outcome', filters.outcome)
    }

    if (filters.severity) {
      params.set('severity', filters.severity)
    }

    if (filters.sla_category) {
      params.set('sla_category', filters.sla_category)
    }
  }

  params.set('page', String(page))

  appendSortParams(params, sort, DEFAULT_JOBS_REPORT_SORT)

  return `?${params.toString()}`
}
